import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { apiClient } from "../../services/api/apiService";
import "../../styles/components/TeamSearchInput.css";

/**
 * TeamSearchInput — searchable opponent team picker for match creation.
 *
 * Shows teams of the same sport, with smart city-first sorting:
 *  - If team1City is provided → first load shows only teams from that city
 *  - User can toggle to "search all teams" at any time
 *  - Typing searches all teams (name contains query)
 *
 * Props
 *   sportName    string   — e.g. "Volleyball" (used to filter by sport)
 *   team1City    string   — city of Team 1 (from settings.home_city); used for smart default
 *   excludeTeamId number  — Team 1 id, excluded from results
 *   value        { id, name, home_city } | null
 *   onChange     fn(teamObject | null)
 *   required     bool
 */
const TeamSearchInput = ({
  sportName,
  team1City,
  excludeTeamId,
  value,
  onChange,
  required = false,
}) => {
  const [searchText, setSearchText] = useState(value?.name || "");
  const [results, setResults] = useState([]);
  const [allCity, setAllCity] = useState(false);     // false = city-scoped, true = all teams
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSelected, setIsSelected] = useState(!!value?.id);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [cityResultCount, setCityResultCount] = useState(null); // null until first fetch

  const debounce = useRef(null);
  const abortCtrl = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const emit = useCallback((team) => onChange(team), [onChange]);

  // ── Close on outside click ──────────────────────────────────────────────
  useEffect(() => {
    const close = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const calcPos = useCallback(() => {
    if (inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setDropdownPos({ top: r.bottom + 4, left: r.left, width: r.width });
    }
  }, []);

  // ── Fetch teams ──────────────────────────────────────────────────────────
  const fetchTeams = useCallback(
    async (q, cityScoped) => {
      if (abortCtrl.current) abortCtrl.current.abort();
      abortCtrl.current = new AbortController();

      const params = new URLSearchParams({ limit: 15, offset: 0 });
      if (sportName) params.set("sport", sportName);
      if (q.trim()) params.set("search", q.trim());
      if (cityScoped && team1City) params.set("city", team1City);

      setIsLoading(true);
      try {
        const res = await apiClient.get(`/teams?${params}`, {
          signal: abortCtrl.current.signal,
        });
        const raw = res.data?.data?.teams || res.data?.teams || [];
        const filtered = excludeTeamId
          ? raw.filter((t) => Number(t.id) !== Number(excludeTeamId))
          : raw;
        setResults(filtered);
        if (cityScoped) setCityResultCount(filtered.length);
        calcPos();
        setIsOpen(true);
      } catch (err) {
        if (err.name !== "AbortError") {
          setIsLoading(false);
        }
      } finally {
        if (abortCtrl.current && !abortCtrl.current.signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [sportName, team1City, excludeTeamId, calcPos]
  );

  // ── Load city-scoped teams on first focus / mount (when sport known) ────
  const initialLoaded = useRef(false);
  useEffect(() => {
    if (sportName && !initialLoaded.current && !value?.id) {
      initialLoaded.current = true;
      fetchTeams("", !!team1City);
    }
  }, [sportName, team1City, value, fetchTeams]);

  // Re-detect isSelected if value changes from parent
  useEffect(() => {
    if (!value?.id) {
      setIsSelected(false);
      setSearchText("");
    }
  }, [value]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleInput = (e) => {
    const q = e.target.value;
    setSearchText(q);
    setIsSelected(false);
    emit(null);

    clearTimeout(debounce.current);
    if (!q.trim()) {
      // No query → show city-scoped or all teams depending on toggle
      debounce.current = setTimeout(() => fetchTeams("", !allCity ? !!team1City : false), 200);
    } else {
      // Typing → always search all teams (user clearly looking for something specific)
      debounce.current = setTimeout(() => fetchTeams(q, false), 300);
    }
  };

  const handleFocus = () => {
    if (!isSelected) {
      calcPos();
      setIsOpen(true);
      if (results.length === 0) {
        fetchTeams(searchText, !allCity ? !!team1City : false);
      }
    }
  };

  const handleSelect = (team) => {
    setSearchText(team.name);
    setIsSelected(true);
    setIsOpen(false);
    emit({ id: team.id, name: team.name, home_city: team.home_city });
  };

  const handleClear = () => {
    setSearchText("");
    setIsSelected(false);
    setResults([]);
    setIsOpen(false);
    setCityResultCount(null);
    setAllCity(false);
    initialLoaded.current = false;
    emit(null);
  };

  const handleExpandToAll = () => {
    setAllCity(true);
    fetchTeams(searchText, false);
  };

  const showCityBanner = !allCity && team1City && cityResultCount !== null;
  const cityFirstWord = team1City?.split(",")[0] || team1City;

  // ── Portal dropdown ──────────────────────────────────────────────────────
  const dropdown =
    isOpen &&
    ReactDOM.createPortal(
      <ul
        className="tsi-dropdown"
        role="listbox"
        style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
      >
        {showCityBanner && (
          <li className="tsi-banner" role="presentation">
            <span className="tsi-banner-text">📍 Teams near {cityFirstWord}</span>
            {cityResultCount === 0 && (
              <button type="button" className="tsi-expand-btn" onMouseDown={handleExpandToAll}>
                Search all teams
              </button>
            )}
          </li>
        )}
        {isLoading && (
          <li className="tsi-loading" role="presentation">Searching…</li>
        )}
        {!isLoading && results.length === 0 && (
          <li className="tsi-empty" role="presentation">
            {showCityBanner
              ? `No ${sportName || ""} teams found near ${cityFirstWord}.`
              : `No teams found.`}
          </li>
        )}
        {results.map((team) => (
          <li key={team.id} role="option">
            <button
              type="button"
              className="tsi-option"
              onMouseDown={() => handleSelect(team)}
            >
              <span className="tsi-team-icon">🏅</span>
              <div className="tsi-option-text">
                <span className="tsi-team-name">{team.name}</span>
                {team.home_city && (
                  <span className="tsi-team-city">{team.home_city}</span>
                )}
              </div>
              {team.active_members_count > 0 && (
                <span className="tsi-member-count">{team.active_members_count} members</span>
              )}
            </button>
          </li>
        ))}
        {!allCity && team1City && results.length > 0 && !isLoading && (
          <li className="tsi-expand-row" role="presentation">
            <button type="button" className="tsi-expand-btn" onMouseDown={handleExpandToAll}>
              🔍 Not here? Search all teams
            </button>
          </li>
        )}
      </ul>,
      document.body
    );

  return (
    <div className="tsi-wrapper" ref={containerRef}>
      {dropdown}
      <div className="tsi-input-wrap" ref={inputRef}>
        <span className="tsi-lead-icon">
          {isSelected ? "🏅" : "🔍"}
        </span>
        <input
          type="text"
          value={searchText}
          onChange={handleInput}
          onFocus={handleFocus}
          placeholder={
            sportName
              ? `Search ${sportName} teams…`
              : "Search teams…"
          }
          className={`form-input-gaming tsi-input${isSelected ? " tsi-input--selected" : ""}`}
          autoComplete="off"
          required={required && !isSelected}
        />
        {isLoading && <span className="tsi-spinner" />}
        {searchText && !isLoading && (
          <button type="button" className="tsi-clear" onClick={handleClear} title="Clear">
            ✕
          </button>
        )}
      </div>
      {isSelected && value?.home_city && (
        <p className="tsi-city-hint">📍 {value.home_city}</p>
      )}
    </div>
  );
};

export default TeamSearchInput;
